import React, { useCallback, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useEditor, BLOCK_TYPES } from '../EditorContext';
import { Columns, Plus, Trash2, GripVertical, PlusCircle, MinusCircle } from 'lucide-react';
import TextBlock from './TextBlock';
import ImageBlock from './ImageBlock';
import ChartBlock from './ChartBlock';

// Drag type for nested blocks
const NESTED_BLOCK_TYPE = 'NESTED_BLOCK';

// Preset column layouts
const COLUMN_PRESETS = {
  '1': [100],
  '2-equal': [50, 50],
  '2-left': [66, 34],
  '2-right': [34, 66],
  '3-equal': [33.33, 33.33, 33.34],
  '4-equal': [25, 25, 25, 25],
};

// Draggable Nested Block Component
function DraggableNestedBlock({ 
  nestedBlock, 
  columnIndex, 
  blockIndex, 
  isEditing, 
  chartData,
  onRemove,
  onMoveBlock,
  parentBlockId,
}) {
  const ref = useRef(null);
  
  const [{ isDragging }, drag, preview] = useDrag({
    type: NESTED_BLOCK_TYPE,
    item: () => ({
      id: nestedBlock.id,
      columnIndex,
      blockIndex,
      parentBlockId,
      block: nestedBlock,
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: isEditing,
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: NESTED_BLOCK_TYPE,
    hover: (item, monitor) => {
      if (!ref.current) return;
      
      // Don't replace items with themselves
      if (item.id === nestedBlock.id) return;
      
      // Only handle hover for same parent
      if (item.parentBlockId !== parentBlockId) return;
      
      const dragIndex = item.blockIndex;
      const hoverIndex = blockIndex;
      const dragColumnIndex = item.columnIndex;
      const hoverColumnIndex = columnIndex;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex && dragColumnIndex === hoverColumnIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      if (dragColumnIndex === hoverColumnIndex) {
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      }

      // Time to actually perform the action
      onMoveBlock(dragColumnIndex, dragIndex, hoverColumnIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      item.blockIndex = hoverIndex;
      item.columnIndex = hoverColumnIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  // Combine drag and drop refs
  drag(drop(ref));

  const renderBlockContent = () => {
    switch (nestedBlock.type) {
      case BLOCK_TYPES.TEXT:
        return <TextBlock block={nestedBlock} />;
      case BLOCK_TYPES.IMAGE:
        return <ImageBlock block={nestedBlock} />;
      case BLOCK_TYPES.CHART:
        return <ChartBlock block={nestedBlock} chartData={chartData} />;
      default:
        return <div className="p-2 bg-yellow-50 text-yellow-800">Unknown block type</div>;
    }
  };

  return (
    <div
      ref={ref}
      className={`nested-block-wrapper relative group/nested mb-2 ${
        isDragging ? 'opacity-50' : ''
      } ${isOver && canDrop ? 'border-t-2 border-blue-500' : ''}`}
      style={{ cursor: isEditing ? 'move' : 'default' }}
    >
      {/* Drag Handle */}
      {isEditing && (
        <div className="absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center opacity-0 group-hover/nested:opacity-100 cursor-grab z-10">
          <GripVertical size={14} className="text-gray-400" />
        </div>
      )}
      
      {/* Block Content */}
      <div className={isEditing ? 'ml-6' : ''}>
        {renderBlockContent()}
      </div>

      {/* Remove Button */}
      {isEditing && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(columnIndex, blockIndex);
          }}
          className="absolute top-0 right-0 p-1 bg-red-100 rounded opacity-0 group-hover/nested:opacity-100 z-10"
          title="Remove block"
        >
          <Trash2 size={14} className="text-red-500" />
        </button>
      )}
    </div>
  );
}

// Drop Zone for empty columns or end of column
function ColumnDropZone({ columnIndex, parentBlockId, onDropBlock, isEditing }) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: NESTED_BLOCK_TYPE,
    drop: (item) => {
      if (item.parentBlockId === parentBlockId) {
        // Moving within same columns block
        onDropBlock(item.columnIndex, item.blockIndex, columnIndex, -1);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  if (!isEditing) return null;

  return (
    <div
      ref={drop}
      className={`min-h-[40px] border-2 border-dashed rounded transition-colors ${
        isOver && canDrop 
          ? 'border-blue-500 bg-blue-50' 
          : 'border-transparent'
      }`}
    />
  );
}

// Column Width Resizer
function ColumnResizer({ onResize, isEditing }) {
  const [isDragging, setIsDragging] = useState(false);
  const startX = useRef(0);

  const handleMouseDown = (e) => {
    if (!isEditing) return;
    e.preventDefault();
    setIsDragging(true);
    startX.current = e.clientX;

    const handleMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX.current;
      onResize(delta);
      startX.current = moveEvent.clientX;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  if (!isEditing) return null;

  return (
    <div
      className={`column-resizer w-2 cursor-col-resize hover:bg-blue-300 transition-colors ${
        isDragging ? 'bg-blue-500' : 'bg-gray-200'
      }`}
      onMouseDown={handleMouseDown}
      style={{ 
        position: 'relative',
        zIndex: 10,
        marginLeft: '-4px',
        marginRight: '-4px',
      }}
    />
  );
}

function ColumnsBlock({ block, chartData }) {
  const { updateBlock, editingBlockId, setEditingBlock } = useEditor();
  const containerRef = useRef(null);

  const columns = block.content?.columns || [];
  const gap = block.settings?.gap || 16;

  // Check if this ColumnsBlock or any of its nested blocks is being edited
  const isColumnsBlockEditing = editingBlockId === block.id;
  const nestedBlockIds = columns.flatMap(col => (col.blocks || []).map(b => b.id));
  const isNestedBlockEditing = nestedBlockIds.includes(editingBlockId);
  
  // ColumnsBlock is "active" if it or any nested block is being edited
  const isActive = isColumnsBlockEditing || isNestedBlockEditing;

  // Apply preset layout
  const applyPreset = useCallback((presetKey) => {
    const widths = COLUMN_PRESETS[presetKey];
    const newColumns = widths.map((width, index) => ({
      id: `col-${Date.now()}-${index}`,
      width,
      blocks: columns[index]?.blocks || [],
    }));

    updateBlock(block.id, {
      content: {
        ...block.content,
        columns: newColumns,
        preset: presetKey,
      },
    });
  }, [block.id, block.content, columns, updateBlock]);

  // Add a new column
  const addColumn = useCallback(() => {
    const newColumns = [...columns];
    const newWidth = 100 / (columns.length + 1);
    
    // Redistribute widths
    const redistributedColumns = newColumns.map(col => ({
      ...col,
      width: newWidth,
    }));
    
    redistributedColumns.push({
      id: `col-${Date.now()}`,
      width: newWidth,
      blocks: [],
    });

    updateBlock(block.id, {
      content: {
        ...block.content,
        columns: redistributedColumns,
        preset: null, // Clear preset when manually adding
      },
    });
  }, [block.id, block.content, columns, updateBlock]);

  // Remove a column
  const removeColumn = useCallback((columnIndex) => {
    if (columns.length <= 1) return;
    
    const newColumns = columns.filter((_, index) => index !== columnIndex);
    const newWidth = 100 / newColumns.length;
    
    // Redistribute widths
    const redistributedColumns = newColumns.map(col => ({
      ...col,
      width: newWidth,
    }));

    updateBlock(block.id, {
      content: {
        ...block.content,
        columns: redistributedColumns,
        preset: null,
      },
    });
  }, [block.id, block.content, columns, updateBlock]);

  // Resize column
  const resizeColumn = useCallback((columnIndex, delta) => {
    if (!containerRef.current) return;
    
    const containerWidth = containerRef.current.offsetWidth;
    const deltaPercent = (delta / containerWidth) * 100;
    
    const newColumns = [...columns];
    const nextIndex = columnIndex + 1;
    
    if (nextIndex >= columns.length) return;
    
    const minWidth = 15; // Minimum 15% width
    
    let newWidth = newColumns[columnIndex].width + deltaPercent;
    let nextWidth = newColumns[nextIndex].width - deltaPercent;
    
    // Enforce minimum widths
    if (newWidth < minWidth) {
      nextWidth += (newWidth - minWidth);
      newWidth = minWidth;
    }
    if (nextWidth < minWidth) {
      newWidth += (nextWidth - minWidth);
      nextWidth = minWidth;
    }
    
    newColumns[columnIndex] = { ...newColumns[columnIndex], width: newWidth };
    newColumns[nextIndex] = { ...newColumns[nextIndex], width: nextWidth };

    updateBlock(block.id, {
      content: {
        ...block.content,
        columns: newColumns,
        preset: null,
      },
    });
  }, [block.id, block.content, columns, updateBlock]);

  // Add block to column
  const addBlockToColumn = useCallback((columnIndex, blockType) => {
    const newColumns = [...columns];
    const newBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: blockType,
      content: blockType === BLOCK_TYPES.TEXT 
        ? { html: '<p>New text block</p>' } 
        : {},
    };

    if (!newColumns[columnIndex].blocks) {
      newColumns[columnIndex].blocks = [];
    }
    newColumns[columnIndex].blocks.push(newBlock);

    updateBlock(block.id, {
      content: {
        ...block.content,
        columns: newColumns,
      },
    });
  }, [block.id, block.content, columns, updateBlock]);

  // Remove block from column
  const removeBlockFromColumn = useCallback((columnIndex, blockIndex) => {
    const newColumns = [...columns];
    newColumns[columnIndex].blocks.splice(blockIndex, 1);

    updateBlock(block.id, {
      content: {
        ...block.content,
        columns: newColumns,
      },
    });
  }, [block.id, block.content, columns, updateBlock]);

  // Move block within or between columns
  const moveBlock = useCallback((fromColIndex, fromBlockIndex, toColIndex, toBlockIndex) => {
    const newColumns = columns.map(col => ({
      ...col,
      blocks: [...(col.blocks || [])],
    }));

    // Get the block being moved
    const [movedBlock] = newColumns[fromColIndex].blocks.splice(fromBlockIndex, 1);

    // If dropping at end of column (toBlockIndex === -1)
    if (toBlockIndex === -1) {
      newColumns[toColIndex].blocks.push(movedBlock);
    } else {
      // Adjust index if moving within same column
      const adjustedIndex = fromColIndex === toColIndex && fromBlockIndex < toBlockIndex 
        ? toBlockIndex - 1 
        : toBlockIndex;
      newColumns[toColIndex].blocks.splice(adjustedIndex, 0, movedBlock);
    }

    updateBlock(block.id, {
      content: {
        ...block.content,
        columns: newColumns,
      },
    });
  }, [block.id, block.content, columns, updateBlock]);

  // Adjust individual column width
  const adjustColumnWidth = useCallback((columnIndex, newWidth) => {
    const newColumns = [...columns];
    const oldWidth = newColumns[columnIndex].width;
    const diff = newWidth - oldWidth;
    
    // Distribute the difference to other columns proportionally
    const otherColumns = newColumns.filter((_, i) => i !== columnIndex);
    const totalOtherWidth = otherColumns.reduce((sum, col) => sum + col.width, 0);
    
    newColumns.forEach((col, i) => {
      if (i === columnIndex) {
        col.width = newWidth;
      } else {
        col.width -= (diff * col.width / totalOtherWidth);
      }
    });

    updateBlock(block.id, {
      content: {
        ...block.content,
        columns: newColumns,
        preset: null,
      },
    });
  }, [block.id, block.content, columns, updateBlock]);

  // Handle click to select this columns block
  const handleColumnsBlockClick = useCallback((e) => {
    // Only set ColumnsBlock as editing when clicking directly on container/controls areas
    // Not when clicking on nested block content (TextBlock, ImageBlock, etc.)
    const clickedOnNestedContent = e.target.closest('.text-block-editor') || 
                                   e.target.closest('.image-block') ||
                                   e.target.closest('.chart-block');
    
    if (!clickedOnNestedContent) {
      setEditingBlock(block.id);
    }
  }, [block.id, setEditingBlock]);

  return (
    <div 
      ref={containerRef}
      className={`columns-block ${isActive ? 'editing' : ''}`}
      onClick={handleColumnsBlockClick}
    >
      {/* Columns Controls - Visible when ColumnsBlock itself is selected */}
      {isColumnsBlockEditing && (
        <div className="columns-controls flex items-center gap-4 mb-2 p-2 bg-gray-100 rounded-lg border flex-wrap">
          {/* Layout Presets */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Layout:</label>
            <div className="flex gap-1">
              {Object.keys(COLUMN_PRESETS).map((preset) => (
                <button
                  key={preset}
                  onClick={() => applyPreset(preset)}
                  className={`px-2 py-1 text-xs border rounded ${
                    block.content?.preset === preset 
                      ? 'bg-blue-100 border-blue-300' 
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {preset.replace('-', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Add/Remove Column */}
          <div className="flex items-center gap-2 border-l pl-4">
            <button
              onClick={addColumn}
              className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-green-50"
              title="Add column"
            >
              <PlusCircle size={14} />
              Add Col
            </button>
          </div>

          {/* Gap Control */}
          <div className="flex items-center gap-2 border-l pl-4">
            <label className="text-xs text-gray-600">Gap:</label>
            <input
              type="number"
              value={gap}
              onChange={(e) => updateBlock(block.id, {
                settings: {
                  ...block.settings,
                  gap: parseInt(e.target.value),
                },
              })}
              min="0"
              max="64"
              className="w-16 text-sm border rounded px-2 py-1"
            />
            <span className="text-xs text-gray-500">px</span>
          </div>

          {/* Column Width Inputs */}
          <div className="flex items-center gap-2 border-l pl-4">
            <label className="text-xs text-gray-600">Widths:</label>
            {columns.map((col, i) => (
              <input
                key={col.id}
                type="number"
                value={Math.round(col.width)}
                onChange={(e) => adjustColumnWidth(i, parseFloat(e.target.value))}
                min="10"
                max="90"
                className="w-12 text-xs border rounded px-1 py-1 text-center"
                title={`Column ${i + 1} width`}
              />
            ))}
            <span className="text-xs text-gray-500">%</span>
          </div>
        </div>
      )}

      {/* Columns Grid */}
      <div 
        className="columns-grid"
        style={{ gap: `${gap}px` }}
      >
        {columns.map((column, columnIndex) => {
          // Calculate width accounting for gap
          const numColumns = columns.length;
          const gapAdjustment = gap * (numColumns - 1) / numColumns;
          const adjustedWidth = `calc(${column.width}% - ${gapAdjustment}px)`;
          
          return (
            <React.Fragment key={column.id}>
              <div
                className="column"
                style={{ 
                  width: adjustedWidth,
                  minHeight: isActive ? '100px' : 'auto',
                }}
              >
                {/* Column Header with remove button */}
                {isColumnsBlockEditing && columns.length > 1 && (
                  <div className="flex justify-end mb-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeColumn(columnIndex);
                      }}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                      title="Remove column"
                    >
                      <MinusCircle size={14} />
                    </button>
                  </div>
                )}

                {/* Column Content */}
                <div className={`column-content ${isActive ? 'border border-dashed border-gray-300 rounded-lg p-2' : ''}`}>
                  {column.blocks?.map((nestedBlock, blockIndex) => (
                    <DraggableNestedBlock
                      key={nestedBlock.id}
                      nestedBlock={nestedBlock}
                      columnIndex={columnIndex}
                      blockIndex={blockIndex}
                      isEditing={isActive}
                      chartData={chartData}
                      onRemove={removeBlockFromColumn}
                      onMoveBlock={moveBlock}
                      parentBlockId={block.id}
                    />
                  ))}

                  {/* Drop Zone at end of column */}
                  <ColumnDropZone
                    columnIndex={columnIndex}
                    parentBlockId={block.id}
                    onDropBlock={moveBlock}
                    isEditing={isActive}
                  />

                  {/* Add Block Buttons */}
                  {isActive && (
                    <div className="add-block-buttons mt-2 flex gap-2 flex-wrap">
                      <button
                        onClick={() => addBlockToColumn(columnIndex, BLOCK_TYPES.TEXT)}
                        className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-blue-50"
                      >
                        <Plus size={12} />
                        Text
                      </button>
                      <button
                        onClick={() => addBlockToColumn(columnIndex, BLOCK_TYPES.IMAGE)}
                        className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-blue-50"
                      >
                        <Plus size={12} />
                        Image
                      </button>
                      <button
                        onClick={() => addBlockToColumn(columnIndex, BLOCK_TYPES.CHART)}
                        className="flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-blue-50"
                      >
                        <Plus size={12} />
                        Chart
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Column Resizer (between columns) */}
              {columnIndex < columns.length - 1 && (
                <ColumnResizer
                  onResize={(delta) => resizeColumn(columnIndex, delta)}
                  isEditing={isActive}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Empty State */}
      {columns.length === 0 && (
        <div className="empty-columns flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
          <Columns size={48} className="text-gray-400 mb-4" />
          <p className="text-gray-500 mb-4">Select a column layout or add columns</p>
          <div className="flex gap-2 flex-wrap justify-center">
            {Object.keys(COLUMN_PRESETS).map((preset) => (
              <button
                key={preset}
                onClick={() => applyPreset(preset)}
                className="px-3 py-2 text-sm border rounded-lg hover:bg-blue-50"
              >
                {preset.replace('-', ' ')}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ColumnsBlock;
